import React, { useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Avatar,
  Skeleton,
} from "@mui/material";
import {
  Inventory as ProductsIcon,
  ShoppingCart as OrdersIcon,
  People as UsersIcon,
  Visibility as ViewIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../hooks/redux";
import {
  fetchRecentOrders,
  fetchTopSellingProducts,
  fetchNearOutOfStockProducts,
  fetchMonthWiseOrders,
  fetchDashboardCountData,
  fetchRecentAgents,
  fetchTopAgentsByOrders,
  fetchTopAgentsByReedeemedPoints,
} from "../store/slices/dashboardSlice";
import SkeletonLoader from "../components/common/SkeletonLoader";
import { categoryAPI } from "../services/api";
import StatusChip from "../utils/StatusChip";
import type { RecentAgent } from "../store/slices/dashboardSlice";

interface StatsCardProps {
  title: string;
  value: number | string;
  icon: React.ReactNode;
  color: "primary" | "secondary" | "info" | "success" | "warning" | "error";
  onClick?: () => void;
}

const StatsCard: React.FC<StatsCardProps> = ({
  title,
  value,
  icon,
  color,
  onClick,
}) => {
  return (
    <Card
      sx={{
        bgcolor: `${color}.main`,
        color: "white",
        height: "100%",
        boxShadow: 3,
        cursor: onClick ? "pointer" : "default",
        "&:hover": {
          boxShadow: 6,
          transform: onClick ? "translateY(-2px)" : "none",
          transition: "all 0.3s ease-in-out",
        },
      }}
      onClick={onClick}
    >
      <CardContent>
        <Box display="flex" alignItems="center">
          <Box mr={2}>{icon}</Box>
          <Box>
            <Typography
              variant="h5"
              sx={{ fontWeight: 800, lineHeight: 1, letterSpacing: 0.3 }}
            >
              {value}
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, color: "grey.100" }}
            >
              {title}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);
  const {
    recentOrders,
    topSellingProducts,
    recentOrdersLoading,
    topSellingProductsLoading,
    error,
    nearOutOfStockProducts,
    nearOutOfStockProductsLoading,
    monthWiseOrders,
    monthWiseOrdersLoading,
    dashboardCountData,
    dashboardCountDataLoading,
    recentAgents,
    recentAgentsLoading,
    topAgentsbyOrders,
    topAgentsbyOrdersLoading,
    topAgentsbyReedeemedPoints,
    topAgentsbyReedeemedPointsLoading,
  } = useAppSelector((state) => state.dashboard);

  useEffect(() => {
    dispatch(fetchRecentOrders());
    dispatch(fetchTopSellingProducts());
    dispatch(fetchNearOutOfStockProducts());
    dispatch(fetchMonthWiseOrders());
    dispatch(fetchDashboardCountData());
    dispatch(fetchRecentAgents());
    dispatch(fetchTopAgentsByOrders());
    dispatch(fetchTopAgentsByReedeemedPoints());
  }, [dispatch]);

  // Navigation handlers
  const handleNavigateToOrders = () => navigate("/orders");
  const handleNavigateToProducts = () => navigate("/products");
  const handleNavigateToAgents = () => navigate("/agent");

  const stats = [
    {
      title: "Total Products",
      value: dashboardCountData?.total_products || 0,
      icon: <ProductsIcon sx={{ fontSize: 40 }} />,
      color: "primary" as const,
      onClick: handleNavigateToProducts,
    },
    {
      title: "Total Orders",
      value: dashboardCountData?.total_orders || 0,
      icon: <OrdersIcon sx={{ fontSize: 40 }} />,
      color: "secondary" as const,
      onClick: handleNavigateToOrders,
    },
    {
      title: "Total Agents",
      value: dashboardCountData?.total_agents || 0,
      icon: <UsersIcon sx={{ fontSize: 40 }} />,
      color: "info" as const,
      onClick: handleNavigateToAgents,
    },
  ];

  const getAgentStatus = (agent: RecentAgent | any): 1 | 0 => {
    const status = agent?.userstatus;

    // numeric or numeric-string check
    if (status === 1 || status === "1") return 1;

    // string checks (case-insensitive)
    if (typeof status === "string") {
      const s = status.trim().toLowerCase();
      if (s === "active" || s === "activated" || s === "active") return 1;
      if (s === "deactive" || s === "deactivated" || s === "inactive") return 0;
    }

    // fallback: treat unknown as deactive (0)
    return 0;
  };

   
  return (
    <Box>
      {/* <Typography variant="h4" gutterBottom sx={{ mb: 4, fontWeight: 'bold', color: 'primary.main' }}>
        Dashboard
      </Typography> */}

      {/* Main Stats Cards */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, 1fr)",
            md: "repeat(3, 1fr)",
          },
          gap: 3,
          mb: 4,
        }}
      >
        {dashboardCountDataLoading
          ? // Render individual skeleton cards to match the grid layout
            Array.from({ length: 3 }).map((_, index) => (
              <Card
                key={index}
                sx={{
                  bgcolor: "grey.100",
                  height: "100%",
                  boxShadow: 3,
                  "&:hover": {
                    boxShadow: 6,
                    transform: "translateY(-2px)",
                    transition: "all 0.3s ease-in-out",
                  },
                }}
              >
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Box mr={2}>
                      <Skeleton variant="circular" width={40} height={40} />
                    </Box>
                    <Box>
                      <Skeleton
                        variant="text"
                        width={80}
                        height={48}
                        sx={{ fontSize: "2rem", fontWeight: "bold" }}
                      />
                      <Skeleton
                        variant="text"
                        width={120}
                        height={20}
                        sx={{ fontSize: "0.875rem" }}
                      />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ))
          : stats.map((stat, index) => <StatsCard key={index} {...stat} />)}
      </Box>

      {/* New Dashboard Sections */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "repeat(3, 1fr)" },
          gap: 3,
          mb: 4,
        }}
      >
        {/* Current Month's Total Orders */}
        <Card sx={{ boxShadow: 3, "&:hover": { boxShadow: 6 } }}>
          <CardContent>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography
                variant="h6"
                fontWeight={600}
                color="primary.main"
                sx={{ fontSize: "1rem", letterSpacing: 0.4 }}
              >
                Current Month's Total Orders
              </Typography>
              <Button
                variant="contained"
                size="medium"
                sx={{
                  bgcolor: "#f6f2ff",
                  color: "#2d117b",
                  fontWeight: 600,
                  px: 2.5,
                  py: 1,
                  fontSize: "1rem",
                  borderRadius: 2,
                  minWidth: 100,
                  width: "auto",
                  boxShadow: 2,
                  textTransform: "none",
                  alignSelf: "flex-end",
                  "&:hover": { bgcolor: "#e0d7fa", color: "#2d117b" },
                }}
                startIcon={<ViewIcon />}
                onClick={handleNavigateToOrders}
              >
                See All
              </Button>
            </Box>
            <Typography
              variant="h5"
              sx={{ fontWeight: 800, mb: 3, color: "primary.main" }}
            >
              {monthWiseOrdersLoading ? (
                <Skeleton variant="text" width="80px" height="60px" />
              ) : monthWiseOrders ? (
                monthWiseOrders.total || 0
              ) : (
                0
              )}
            </Typography>

            {/* Order Status Boxes */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: 2,
                minWidth: 0,
                boxSizing: "border-box",
                overflow: "initial",
              }}
            >
              {monthWiseOrdersLoading ? (
                <SkeletonLoader type="monthWiseOrders" count={1} />
              ) : monthWiseOrders ? (
                <>
                  {/* First row: Pending, Confirmed, In Progress */}
                  <Box sx={{ gridColumn: "span 3", display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 2 }}>
                    <Box sx={{ bgcolor: "secondary.light", color: "secondary.dark", p: 2, borderRadius: 2, textAlign: "center", border: "2px solid", borderColor: "secondary.main", minWidth: 0, boxSizing: "border-box" }}>
                      <Typography variant="overline" sx={{ fontWeight: 700, mb: 0.5, display: "block" }}>Pending</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 800 }}>{monthWiseOrders.pending || 0}</Typography>
                    </Box>
                    <Box sx={{ bgcolor: "info.light", color: "info.dark", p: 2, borderRadius: 2, textAlign: "center", border: "2px solid", borderColor: "info.main", minWidth: 0, boxSizing: "border-box" }}>
                      <Typography variant="overline" sx={{ fontWeight: 700, mb: 0.5, display: "block" }}>Confirmed</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 800 }}>{monthWiseOrders.confirmed || 0}</Typography>
                    </Box>
                    <Box sx={{ bgcolor: "warning.light", color: "warning.dark", p: 2, borderRadius: 2, textAlign: "center", border: "2px solid", borderColor: "warning.main", minWidth: 0, boxSizing: "border-box" }}>
                      <Typography variant="overline" sx={{ fontWeight: 700, mb: 0.5, display: "block" }}>In Progress</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 800 }}>{monthWiseOrders.in_progress || 0}</Typography>
                    </Box>
                  </Box>
                  {/* Second row: Delivered, Cancelled, Rejected */}
                  <Box sx={{ gridColumn: "span 3", display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 2, mt: 2 }}>
                    <Box sx={{ bgcolor: "success.light", color: "success.dark", p: 2, borderRadius: 2, textAlign: "center", border: "2px solid", borderColor: "success.main", minWidth: 0, boxSizing: "border-box" }}>
                      <Typography variant="overline" sx={{ fontWeight: 700, mb: 0.5, display: "block" }}>Delivered</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 800 }}>{monthWiseOrders.delivered || 0}</Typography>
                    </Box>
                    <Box sx={{ bgcolor: "error.light", color: "error.dark", p: 2, borderRadius: 2, textAlign: "center", border: "2px solid", borderColor: "error.main", minWidth: 0, boxSizing: "border-box" }}>
                      <Typography variant="overline" sx={{ fontWeight: 700, mb: 0.5, display: "block" }}>Cancelled</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 800 }}>{monthWiseOrders.cancelled || 0}</Typography>
                    </Box>
                    <Box sx={{ bgcolor: "grey.200", color: "grey.800", p: 2, borderRadius: 2, textAlign: "center", border: "2px solid", borderColor: "grey.400", minWidth: 0, boxSizing: "border-box" }}>
                      <Typography variant="overline" sx={{ fontWeight: 700, mb: 0.5, display: "block" }}>Rejected</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 800 }}>{monthWiseOrders.rejected || 0}</Typography>
                    </Box>
                  </Box>
                </>
              ) : (
                <>
                  {/* First row: Pending, Confirmed, In Progress */}
                  <Box sx={{ gridColumn: "span 3", display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 2 }}>
                    <Box sx={{ bgcolor: "secondary.light", color: "secondary.dark", p: 2, borderRadius: 2, textAlign: "center", border: "2px solid", borderColor: "secondary.main" }}>
                      <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>Pending</Typography>
                      <Typography variant="h4" fontWeight="bold">0</Typography>
                    </Box>
                    <Box sx={{ bgcolor: "info.light", color: "info.dark", p: 2, borderRadius: 2, textAlign: "center", border: "2px solid", borderColor: "info.main" }}>
                      <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>Confirmed</Typography>
                      <Typography variant="h4" fontWeight="bold">0</Typography>
                    </Box>
                    <Box sx={{ bgcolor: "warning.light", color: "warning.dark", p: 2, borderRadius: 2, textAlign: "center", border: "2px solid", borderColor: "warning.main" }}>
                      <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>In Progress</Typography>
                      <Typography variant="h4" fontWeight="bold">0</Typography>
                    </Box>
                  </Box>
                  {/* Second row: Delivered, Cancelled, Rejected */}
                  <Box sx={{ gridColumn: "span 3", display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 2, mt: 2 }}>
                    <Box sx={{ bgcolor: "success.light", color: "success.dark", p: 2, borderRadius: 2, textAlign: "center", border: "2px solid", borderColor: "success.main" }}>
                      <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>Delivered</Typography>
                      <Typography variant="h4" fontWeight="bold">0</Typography>
                    </Box>
                    <Box sx={{ bgcolor: "error.light", color: "error.dark", p: 2, borderRadius: 2, textAlign: "center", border: "2px solid", borderColor: "error.main" }}>
                      <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>Cancelled</Typography>
                      <Typography variant="h4" fontWeight="bold">0</Typography>
                    </Box>
                    <Box sx={{ bgcolor: "grey.200", color: "grey.800", p: 2, borderRadius: 2, textAlign: "center", border: "2px solid", borderColor: "grey.400" }}>
                      <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>Rejected</Typography>
                      <Typography variant="h4" fontWeight="bold">0</Typography>
                    </Box>
                  </Box>
                </>
              )}
            </Box>
          </CardContent>
        </Card>

        {/* Recent Orders */}
        <Card sx={{ boxShadow: 3, "&:hover": { boxShadow: 6 } }}>
          <CardContent>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography
                variant="h6"
                fontWeight={600}
                color="primary.main"
                sx={{ fontSize: "1rem", letterSpacing: 0.4 }}
              >
                Recent Orders
              </Typography>
              <Button
                variant="contained"
                size="medium"
                sx={{
                  bgcolor: "#f6f2ff",
                  color: "#2d117b",
                  fontWeight: 600,
                  px: 2.5,
                  py: 1,
                  fontSize: "1rem",
                  borderRadius: 2,
                  minWidth: 100,
                  width: "auto",
                  boxShadow: 2,
                  textTransform: "none",
                  alignSelf: "flex-end",
                  "&:hover": { bgcolor: "#e0d7fa", color: "#2d117b" },
                }}
                startIcon={<ViewIcon />}
                onClick={handleNavigateToOrders}
              >
                See All
              </Button>
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {recentOrdersLoading ? (
                <SkeletonLoader type="recentOrders" count={3} />
              ) : recentOrders.length > 0 ? (
                recentOrders.map((order) => (
                  <Box
                    key={order.id}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      p: 1,
                      bgcolor: "grey.50",
                      borderRadius: 1,
                    }}
                  >
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {order.oc_cart_order_id ? order.oc_cart_order_id : order.orderno ? order.orderno : "N/A"}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 500, color: "text.secondary" }}
                      >
                        {order.totalamount} Points
                      </Typography>
                    </Box>
                    <StatusChip status={order.orderstatus} isOrder={true} />
                  </Box>
                ))
              ) : error ? (
                <Typography
                  variant="body2"
                  color="error"
                  sx={{ textAlign: "center", py: 2 }}
                >
                  Error loading recent orders: {error}
                </Typography>
              ) : (
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{ color: "text.secondary", textAlign: "center", py: 2 }}
                >
                  No recent orders found
                </Typography>
              )}
            </Box>
          </CardContent>
        </Card>

        {/* Top Selling Products */}
        <Card sx={{ boxShadow: 3, "&:hover": { boxShadow: 6 } }}>
          <CardContent>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography
                variant="h6"
                fontWeight={600}
                color="primary.main"
                sx={{ fontSize: "1rem", letterSpacing: 0.4 }}
              >
                Top Selling Products
              </Typography>
              <Button
                variant="contained"
                size="medium"
                sx={{
                  bgcolor: "#f6f2ff",
                  color: "#2d117b",
                  fontWeight: 600,
                  px: 2.5,
                  py: 1,
                  fontSize: "1rem",
                  borderRadius: 2,
                  minWidth: 100,
                  width: "auto",
                  boxShadow: 2,
                  textTransform: "none",
                  alignSelf: "flex-end",
                  "&:hover": { bgcolor: "#e0d7fa", color: "#2d117b" },
                }}
                startIcon={<ViewIcon />}
                onClick={handleNavigateToProducts}
              >
                See All
              </Button>
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {topSellingProductsLoading ? (
                <SkeletonLoader type="topSellingProducts" count={3} />
              ) : topSellingProducts.length > 0 ? (
                topSellingProducts.map((product) => (
                  <Box
                    key={product.id}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      p: 1,
                      bgcolor: "grey.50",
                      borderRadius: 1,
                    }}
                  >
                    <Avatar
                      src={
                        product.productmedia?.[0]?.productname
                          ? categoryAPI.getMediaDownloadUrl(
                              product.productmedia[0].productname
                            )
                          : undefined
                      }
                      alt={product.name}
                      sx={{
                        width: 32,
                        height: 32,
                        bgcolor: "primary.main",
                        fontSize: "0.8rem",
                      }}
                      onError={(e) => {
                        console.log(
                          "Image load error for:",
                          product.productmedia?.[0]?.productname
                        );
                        // Just let the avatar show the letter if image fails
                      }}
                    >
                      {product.name.charAt(0).toUpperCase()}
                    </Avatar>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {product.name}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 500, color: "text.secondary" }}
                      >
                        {product.price
                          ? `${product.price.toLocaleString()}`
                          : "0"}{" "}
                        Points
                      </Typography>
                    </Box>
                  </Box>
                ))
              ) : error ? (
                <Typography
                  variant="body2"
                  color="error"
                  sx={{ textAlign: "center", py: 2 }}
                >
                  Error loading top selling products: {error}
                </Typography>
              ) : (
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{ color: "text.secondary", textAlign: "center", py: 2 }}
                >
                  No top selling products found
                </Typography>
              )}
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Bottom Row - New Sections */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "repeat(2, 1fr)" },
          gap: 3,
          mb: 4,
        }}
      >
        {/* Recently Registered Agents */}
        <Card sx={{ boxShadow: 3, "&:hover": { boxShadow: 6 } }}>
          <CardContent>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography
                variant="h6"
                fontWeight={600}
                color="primary.main"
                sx={{ fontSize: "1rem", letterSpacing: 0.4 }}
              >
                Recently Registered Agents
              </Typography>
              <Button
                variant="contained"
                size="medium"
                sx={{
                  bgcolor: "#f6f2ff",
                  color: "#2d117b",
                  fontWeight: 600,
                  px: 2.5,
                  py: 1,
                  fontSize: "1rem",
                  borderRadius: 2,
                  minWidth: 100,
                  width: "auto",
                  boxShadow: 2,
                  textTransform: "none",
                  alignSelf: "flex-end",
                  "&:hover": { bgcolor: "#e0d7fa", color: "#2d117b" },
                }}
                startIcon={<ViewIcon />}
                onClick={handleNavigateToAgents}
              >
                See All
              </Button>
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {recentAgentsLoading ? (
                <SkeletonLoader type="nearOutOfStockProducts" count={3} />
              ) : recentAgents.length > 0 ? (
                recentAgents.map((agent) => {
                  const displayName =
                    [agent.firstname, agent.lastname]
                      .filter(Boolean)
                      .join(" ") ||
                    agent.username ||
                    agent.email ||
                    "Unknown";
                  const avatarLetter =
                    displayName !== "Unknown"
                      ? displayName.charAt(0).toUpperCase()
                      : agent.username
                      ? agent.username.charAt(0).toUpperCase()
                      : "A";
                  const mobile = agent.cellnumber
                    ? agent.countrycode
                      ? `+${agent.countrycode} ${agent.cellnumber}`
                      : agent.cellnumber
                    : "-";
                  const agentCode = agent.agentcode || "-";

                  return (
                    <Box
                      key={agent.id}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        p: 1,
                        bgcolor: "grey.50",
                        borderRadius: 1,
                      }}
                    >
                      <Avatar
                        sx={{
                          width: 40,
                          height: 40,
                          bgcolor: "primary.main",
                          fontWeight: "bold",
                        }}
                      >
                        {avatarLetter}
                      </Avatar>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {displayName}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 500, color: "text.secondary" }}
                        >
                          {agentCode}
                        </Typography>
                      </Box>
                      <Box sx={{ minWidth: 120 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {mobile}
                        </Typography>
                      </Box>
                      <StatusChip status={getAgentStatus(agent)} />
                    </Box>
                  );
                })
              ) : error ? (
                <Typography
                  variant="body2"
                  color="error"
                  sx={{ textAlign: "center", py: 2 }}
                >
                  Error loading recent agents: {error}
                </Typography>
              ) : (
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{ color: "text.secondary", textAlign: "center", py: 2 }}
                >
                  No recent agents found
                </Typography>
              )}
            </Box>
          </CardContent>
        </Card>

        {/* Near To Out of Stock Products */}
        <Card sx={{ boxShadow: 3, "&:hover": { boxShadow: 6 } }}>
          <CardContent>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography
                variant="h6"
                fontWeight={600}
                color="primary.main"
                sx={{ fontSize: "1rem", letterSpacing: 0.4 }}
              >
                Near To Out of Stock Products
              </Typography>
              <Button
                variant="contained"
                size="medium"
                sx={{
                  bgcolor: "#f6f2ff",
                  color: "#2d117b",
                  fontWeight: 600,
                  px: 2.5,
                  py: 1,
                  fontSize: "1rem",
                  borderRadius: 2,
                  minWidth: 100,
                  width: "auto",
                  boxShadow: 2,
                  textTransform: "none",
                  alignSelf: "flex-end",
                  "&:hover": { bgcolor: "#e0d7fa", color: "#2d117b" },
                }}
                startIcon={<ViewIcon />}
                onClick={handleNavigateToProducts}
              >
                See All
              </Button>
            </Box>
            {/* Table layout for product info */}
            <Box sx={{ width: "100%", overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: "#f5f7fa" }}>
                    <th
                      style={{
                        textAlign: "left",
                        padding: "16px 12px",
                        fontWeight: 600,
                        fontSize: "1rem",
                        letterSpacing: 1,
                        color: "#7c7c7c",
                      }}
                    >
                      PRODUCTS
                    </th>
                    <th
                      style={{
                        textAlign: "left",
                        padding: "16px 12px",
                        fontWeight: 600,
                        fontSize: "1rem",
                        letterSpacing: 1,
                        color: "#7c7c7c",
                      }}
                    >
                      CATEGORY
                    </th>
                    <th
                      style={{
                        textAlign: "left",
                        padding: "16px 12px",
                        fontWeight: 600,
                        fontSize: "1rem",
                        letterSpacing: 1,
                        color: "#7c7c7c",
                      }}
                    >
                      POINTS
                    </th>
                    <th
                      style={{
                        textAlign: "left",
                        padding: "16px 12px",
                        fontWeight: 600,
                        fontSize: "1rem",
                        letterSpacing: 1,
                        color: "#7c7c7c",
                      }}
                    >
                      QUANTITY
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {nearOutOfStockProductsLoading ? (
                    <tr>
                      <td colSpan={4} style={{ padding: "24px" }}>
                        <SkeletonLoader
                          type="nearOutOfStockProducts"
                          count={3}
                        />
                      </td>
                    </tr>
                  ) : nearOutOfStockProducts.length > 0 ? (
                    nearOutOfStockProducts.map((product, idx) => (
                      <tr
                        key={product.id}
                        style={{
                          background: idx % 2 === 0 ? "#fff" : "#f5f7fa",
                        }}
                      >
                        <td
                          style={{
                            padding: "20px 12px",
                            display: "flex",
                            alignItems: "center",
                            gap: 12,
                            fontSize: ".875rem",
                            fontWeight: 600,
                          }}
                        >
                          {product.productmedia &&
                          product.productmedia.length > 0 ? (
                            <Avatar
                              src={
                                product.productmedia?.[0]?.productname
                                  ? categoryAPI.getMediaDownloadUrl(
                                      product.productmedia[0].productname
                                    )
                                  : undefined
                              }
                              alt={product.name}
                              sx={{
                                width: 36,
                                height: 36,
                                bgcolor: "grey.100",
                                fontSize: "1rem",
                                mr: 1,
                                borderRadius: 2,
                              }}
                              onError={(e) => {
                                // fallback
                              }}
                            >
                              {product.name.charAt(0).toUpperCase()}
                            </Avatar>
                          ) : (
                            <Avatar
                              sx={{
                                width: 36,
                                height: 36,
                                bgcolor: "grey.100",
                                fontSize: "1rem",
                                mr: 1,
                                borderRadius: 2,
                              }}
                            >
                              {product.name.charAt(0).toUpperCase()}
                            </Avatar>
                          )}
                          <span>{product.name}</span>
                        </td>
                        <td
                          style={{
                            padding: "20px 12px",
                            fontSize: ".875rem",
                            fontWeight: 600,
                          }}
                        >
                          {product.category && product.category.name
                            ? product.category.name
                            : "-"}
                        </td>
                        <td
                          style={{
                            padding: "20px 12px",
                            fontSize: ".875rem",
                            fontWeight: 600,
                          }}
                        >
                          {typeof product.price === "number"
                            ? `${product.price}`
                            : "0"}{" "}
                          Points
                        </td>
                        <td
                          style={{
                            padding: "20px 12px",
                            fontSize: ".875rem",
                            fontWeight: 600,
                          }}
                        >
                          {product.availablequantity}
                        </td>
                      </tr>
                    ))
                  ) : error ? (
                    <tr>
                      <td
                        colSpan={4}
                        style={{
                          padding: "24px",
                          color: "red",
                          textAlign: "center",
                          fontSize: "1.1rem",
                        }}
                      >
                        Error loading near out of stock products: {error}
                      </td>
                    </tr>
                  ) : (
                    <tr>
                      <td
                        colSpan={4}
                        style={{
                          padding: "24px",
                          color: "#888",
                          textAlign: "center",
                          fontSize: "1.1rem",
                        }}
                      >
                        No near out of stock products found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Charts Section - Original */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "repeat(2, 1fr)" },
          gap: 3,
        }}
      >
        {/* Top Agents by No of Orders */}
        <Card sx={{ boxShadow: 3, "&:hover": { boxShadow: 6 } }}>
          <CardContent>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography
                variant="h6"
                fontWeight={600}
                color="primary.main"
                sx={{ fontSize: "1rem", letterSpacing: 0.4 }}
              >
                Top Agents by No of Orders
              </Typography>
              <Button
                variant="contained"
                size="medium"
                sx={{
                  bgcolor: "#f6f2ff",
                  color: "#2d117b",
                  fontWeight: 600,
                  px: 2.5,
                  py: 1,
                  fontSize: "1rem",
                  borderRadius: 2,
                  minWidth: 100,
                  width: "auto",
                  boxShadow: 2,
                  textTransform: "none",
                  alignSelf: "flex-end",
                  "&:hover": { bgcolor: "#e0d7fa", color: "#2d117b" },
                }}
                startIcon={<ViewIcon />}
                onClick={handleNavigateToOrders}
              >
                See All
              </Button>
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {topAgentsbyOrdersLoading ? (
                <SkeletonLoader type="recentOrders" count={3} />
              ) : (topAgentsbyOrders?.length ?? 0) > 0 ? (
                (topAgentsbyOrders ?? []).map((agent) => {
                  const displayName =
                    [agent.name, agent.username]
                      .filter(Boolean)
                      .join(" ") ||
                    agent.username ||
                    agent.email ||
                    "Unknown";
                  const avatarLetter =
                    displayName !== "Unknown"
                      ? displayName.charAt(0).toUpperCase()
                      : agent.username
                      ? agent.username.charAt(0).toUpperCase()
                      : "A";
                  const mobile = agent.cellnumber
                    ? agent.countrycode
                      ? `+${agent.countrycode} ${agent.cellnumber}`
                      : agent.cellnumber
                    : "-";
                  const orderCount = agent.orderCount || 0;

                  return (
                    <Box
                      key={agent.id}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        p: 1,
                        bgcolor: "grey.50",
                        borderRadius: 1,
                      }}
                    >
                      <Avatar
                        sx={{
                          width: 40,
                          height: 40,
                          bgcolor: "primary.main",
                          fontWeight: "bold",
                        }}
                      >
                        {avatarLetter}
                      </Avatar>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {displayName}
                        </Typography>
                      </Box>
                      <Box sx={{ minWidth: 120 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {mobile}
                        </Typography>
                      </Box>
                      <Box sx={{ minWidth: 80, textAlign: "right" }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {orderCount} Orders   
                        </Typography>
                      </Box>
                    </Box>
                  );
                })
              ) : error ? (
                <Typography
                  variant="body2"
                  color="error"
                  sx={{ textAlign: "center", py: 2 }}
                >
                  Error loading top agents by orders: {error}
                </Typography>
              ) : (
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{ color: "text.secondary", textAlign: "center", py: 2 }}
                >
                  No top agents by orders found
                </Typography>
              )}
            </Box>
          </CardContent>
        </Card>
        {/* Top Agents by Redeem Points */}
        <Card sx={{ boxShadow: 3, "&:hover": { boxShadow: 6 } }}>
          <CardContent>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography
                variant="h6"
                fontWeight={600}
                color="primary.main"
                sx={{ fontSize: "1rem", letterSpacing: 0.4 }}
              >
                Top Agents by Redeem Points
              </Typography>
              <Button
                variant="contained"
                size="medium"
                sx={{
                  bgcolor: "#f6f2ff",
                  color: "#2d117b",
                  fontWeight: 600,
                  px: 2.5,
                  py: 1,
                  fontSize: "1rem",
                  borderRadius: 2,
                  minWidth: 100,
                  width: "auto",
                  boxShadow: 2,
                  textTransform: "none",
                  alignSelf: "flex-end",
                  "&:hover": { bgcolor: "#e0d7fa", color: "#2d117b" },
                }}
                startIcon={<ViewIcon />}
                onClick={handleNavigateToProducts}
              >
                See All
              </Button>
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {topAgentsbyReedeemedPointsLoading ? (
                <SkeletonLoader type="recentOrders" count={3} />
              ) : (topAgentsbyReedeemedPoints?.length ?? 0) > 0 ? (
                (topAgentsbyReedeemedPoints ?? []).map((agent) => {
                  const displayName =
                    [agent.name, agent.username]
                      .filter(Boolean)
                      .join(" ") ||
                    agent.username ||
                    agent.email ||
                    "Unknown";
                  const avatarLetter =
                    displayName !== "Unknown"
                      ? displayName.charAt(0).toUpperCase()
                      : agent.username
                      ? agent.username.charAt(0).toUpperCase()
                      : "A";
                  const mobile = agent.cellnumber
                    ? agent.countrycode
                      ? `+${agent.countrycode} ${agent.cellnumber}`
                      : agent.cellnumber
                    : "-";
                  const redeemedPoints = agent.redeemedPoints || 0;

                  return (
                    <Box
                      key={agent.id}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        p: 1,
                        bgcolor: "grey.50",
                        borderRadius: 1,
                      }}
                    >
                      <Avatar
                        sx={{
                          width: 40,
                          height: 40,
                          bgcolor: "primary.main",
                          fontWeight: "bold",
                        }}
                      >
                        {avatarLetter}
                      </Avatar>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {displayName}
                        </Typography>
                      </Box>
                      <Box sx={{ minWidth: 120 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {mobile}
                        </Typography>
                      </Box>
                      <Box sx={{ minWidth: 80, textAlign: "right" }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {redeemedPoints.toLocaleString()} Points
                        </Typography>
                      </Box>
                    </Box>
                  );
                })
              ) : error ? (
                <Typography
                  variant="body2"
                  color="error"
                  sx={{ textAlign: "center", py: 2 }}
                >
                  Error loading top agents by redeemed points: {error}
                </Typography>
              ) : (
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{ color: "text.secondary", textAlign: "center", py: 2 }}
                >
                  No top agents by redeemed points found
                </Typography>
              )}
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Recent Activity */}
      {/* <Card sx={{ boxShadow: 3, '&:hover': { boxShadow: 6 }, mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom fontWeight="bold" color="primary.main">
            Recent Activity
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                <OrdersIcon fontSize="small" />
              </Avatar>
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="body2" fontWeight="medium">
                  New order #ORD-006 received from John Smith
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  2 minutes ago
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}>
                <UsersIcon fontSize="small" />
              </Avatar>
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="body2" fontWeight="medium">
                  New agent Sarah Johnson registered
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  15 minutes ago
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Avatar sx={{ width: 32, height: 32, bgcolor: 'info.main' }}>
                <ProductsIcon fontSize="small" />
              </Avatar>
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="body2" fontWeight="medium">
                  Product iPhone 15 Pro stock is running low
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  1 hour ago
                </Typography>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card> */}
    </Box>
  );
};

export default Dashboard;
